/** HFD DK108 的 VID+PID 列表（有线 + 2.4G 接收器） */
export const HFD_DK108_VENDOR_PRODUCT: ReadonlyArray<{
  vendorId: number;
  productId: number;
  identifier: string;
}> = [
  { vendorId: 0x0c45, productId: 0x8801, identifier: "3141:34817:DK108" }, // 有线
  // 2.4G 与 X87 / X75 Pro 共用 65278 接收器，唯一标识是完整后缀 :1:26
  {
    vendorId: 0x0c45,
    productId: 0xfefe,
    identifier: "3141:65278:2.4G Dongle:1:26",
  },
];

export type HfdDk108EquipmentLike = {
  vendorId?: number;
  productId?: number;
  keyboardType?: string;
  displayname?: string;
  productName?: string;
  solutionRovider?: string;
  identifier?: string;
  workMode?: unknown;
};

function dk108NameBlob(eq: HfdDk108EquipmentLike): string {
  return `${eq.displayname ?? ""} ${eq.productName ?? ""} ${eq.keyboardType ?? ""}`;
}

function is24GWorkMode(workMode: unknown): boolean {
  return String(workMode) === "2" || workMode === 2;
}

/** 是否为 HFD DK108 设备（有线 / 2.4G） */
export function isHuafendaDk108Device(
  eq: HfdDk108EquipmentLike | null | undefined
): boolean {
  if (!eq) return false;

  const pid = Number(eq.productId);
  if (pid === 0x8801) return true;

  const kt = String(eq.keyboardType ?? "").trim();
  if (kt === "DK108") return true;

  const nameBlob = dk108NameBlob(eq);
  if (/\bdk108\b/i.test(nameBlob)) return true;

  const id = String(eq.identifier ?? "").trim();
  if (id.includes("34817") || id.includes("DK108")) return true;
  // 2.4G：identifier 已含唯一后缀（如 65278:2.4G Dongle:1:26），勿再依赖 product 字段
  if (id.includes("65278") && /dongle:1:26/i.test(id)) return true;

  const vid = Number(eq.vendorId);
  if (!Number.isFinite(vid) || !id) return false;
  return HFD_DK108_VENDOR_PRODUCT.some(
    (p) =>
      p.vendorId === vid &&
      (p.identifier === id || id.startsWith(`${p.identifier}:`))
  );
}

/** 是否为 HFD DK108（展示名 / 配置表匹配） */
export function isHuafendaDk108Equipment(
  eq: HfdDk108EquipmentLike | null | undefined
): boolean {
  if (!eq || typeof eq !== "object") return false;
  const provider = String(eq.solutionRovider ?? "")
    .trim()
    .toLowerCase();
  if (provider && provider !== "huafenda") return false;
  return isHuafendaDk108Device(eq);
}

/** DK108 有线（USB 0x8801） */
export function isHuafendaDk108Wired(
  eq: HfdDk108EquipmentLike | null | undefined
): boolean {
  return isHuafendaDk108Device(eq) && Number(eq?.productId) === 0x8801;
}

/** DK108 2.4G（接收器 0xfefe，identifier 后缀 Dongle:1:26） */
export function isHuafendaDk108Wireless(
  eq: HfdDk108EquipmentLike | null | undefined
): boolean {
  if (!isHuafendaDk108Device(eq)) return false;
  if (Number(eq?.productId) === 0x8801) return false;
  const wm = eq?.workMode;
  if (is24GWorkMode(wm)) return true;
  const id = String(eq?.identifier ?? "").trim();
  return id.includes("65278") || Number(eq?.productId) === 0xfefe;
}
